import { useState, useContext } from 'react';
import { LoggerContext } from "context/LoggerContext";
import { applyPromoCodeToCart, removePromoCodeFromCart } from '../services/checkoutApiService';

/**
 * Hook for managing promo code functionality
 * @param {Object} params - Parameters for the hook
 * @param {string} params.cartToken - Cart token
 * @param {Function} params.onOrderUpdate - Callback when order is updated
 * @param {Object} params.organization - Organization context data
 * @returns {Object} Promo code management methods and state
 */
const usePromoCodeManager = ({ cartToken, onOrderUpdate, organization }) => {
	const { logError } = useContext(LoggerContext);
	const [activeCoupon, setActiveCoupon] = useState(null);
	const [promoApplied, setPromoApplied] = useState(false);
	const [couponError, setCouponError] = useState("");
	const [isApplyingPromo, setIsApplyingPromo] = useState(false);
	const [showPromoField, setShowPromoField] = useState(false);

	/**
	 * Toggles the visibility of the promo code input field
	 */
	const togglePromoField = () => {
		setShowPromoField(prev => !prev);
		setCouponError("");
	};

	/**
	 * Applies a promo code to the current order
	 */
	const applyPromoCode = async (promoCode) => {
		const code = promoCode.trim();
		if (!code || !cartToken) {
			setCouponError(!code ? "Bitte geben Sie einen Gutscheincode ein" : "Warenkorb konnte nicht geladen werden");
			return { success: false };
		}
		
		setIsApplyingPromo(true);
		setCouponError("");
		
		try {
			const response = await applyPromoCodeToCart({
				cartToken,
				code,
				otk: organization?.otk
			});
			
			if (response.httpStatus === 200 && response.data) {
				if (onOrderUpdate) onOrderUpdate(response.data);
				
				if (response.data.coupon) {
					setActiveCoupon(response.data.coupon);
					setPromoApplied(true);
					setShowPromoField(false);
					return {
						success: true,
						data: response.data
					};
				}
			} else {
				// Set generic error message - specific error will be shown by the ErrorNotification component
				setCouponError("Ungültiger Gutscheincode");
			}
		} catch (error) {
			logError("Error applying promo code:", error);
			setCouponError("Fehler bei der Gutscheinprüfung");
		} finally {
			setIsApplyingPromo(false);
		}
		
		return { success: false };
	};

	/**
	 * Removes the currently applied promo code
	 */
	const removePromoCode = async () => {
		if (!cartToken || !activeCoupon) {
			setPromoApplied(false);
			setActiveCoupon(null);
			return { success: true };
		}
		
		setIsApplyingPromo(true);
		
		try {
			const response = await removePromoCodeFromCart({
				cartToken,
				code: activeCoupon.code,
				otk: organization?.otk
			});

			setPromoApplied(false);
			setActiveCoupon(null);

			if (response.httpStatus === 200 && response.data) {
				if (onOrderUpdate) onOrderUpdate(response.data);
				return {
					success: true,
					data: response.data
				};
			}
			
			// Set generic error for the UI
			setCouponError("Fehler beim Entfernen des Gutscheincodes");
			return { success: false };
		} catch (error) {
			logError("Error removing promo code:", error);
			setCouponError("Fehler beim Entfernen des Gutscheincodes");
			return { success: false };
		} finally {
			setIsApplyingPromo(false);
		}
	};

	return {
		activeCoupon,
		setActiveCoupon,
		promoApplied,
		setPromoApplied,
		couponError,
		setCouponError,
		isApplyingPromo,
		showPromoField,
		setShowPromoField,
		togglePromoField,
		applyPromoCode,
		removePromoCode
	};
};

export default usePromoCodeManager;
